"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.getBorrowingBase = exports.contributesToBorrowingBase = void 0;
const _ = __importStar(require("lodash"));
const common_1 = require("@finley-technologies/common");
const borrowingBase_1 = require("../borrowingBase");
// Helper method to determine if transaction target is routed to a cash account that contributes to the borrowing base
const contributesToBorrowingBase = ({ request, target, contributingAccountTypes = common_1.consts.borrowingBaseContributionAccountTypes, // Default, do not override unless there is an explicit use case
 }) => {
    switch (target) {
        case 'destination':
            return contributingAccountTypes.includes(request.destinationBankAccountType);
        case 'source':
            return contributingAccountTypes.includes(request.sourceBankAccountType);
        default:
            return (0, common_1.finleyAssertUnreachable)(target);
    }
};
exports.contributesToBorrowingBase = contributesToBorrowingBase;
// Helper method to provide the adjustments for a pro forma request (unsettled funding request component)
// Cash adjustments are based solely on the owner of the account (borrower or capital provider)
//    Source account balance is decreased, while destination account balance is increased (cash transferred from source to destination)
// Debt adjustments require the expected routings because the funding request component does not include the target organization
//    Required routing is found based on funding request component type, and matching target bank account ID
const getExpectedAdjustments = (pendingRequest, requestRoutings) => {
    // For all following cash adjustments
    // Borrower owned: adjust the balance (if account contributes to borrowing base, it must be present)
    // Capital provider owned: no-op
    switch (pendingRequest.type) {
        // Draw
        case 'drawRequest': {
            // Find associated request routing
            const routing = requestRoutings.find((requestRouting) => requestRouting.type === pendingRequest.type &&
                requestRouting.source.accountId ===
                    pendingRequest.sourceBankAccountId);
            (0, common_1.finleyAssert)(routing, `Routing for draw request was not found`);
            return {
                // *** Cash ***
                cash: {
                    // Source (capital provider): no-op
                    decreaseSourceAccount: false,
                    // Destination (borrower): increase
                    increaseDestinationAccount: true,
                },
                // *** Debt ***
                // Outstanding balance: increase
                debt: {
                    organizationId: routing.source.organizationId,
                    amount: pendingRequest.amount,
                },
            };
        }
        // Paydown (voluntary)
        case 'collectionsPaydown': {
            // Find associated request routing
            const routing = requestRoutings.find((requestRouting) => requestRouting.type === pendingRequest.type &&
                requestRouting.source.accountId ===
                    pendingRequest.sourceBankAccountId);
            (0, common_1.finleyAssert)(routing, `Routing for collections paydown was not found`);
            return {
                // *** Cash ***
                cash: {
                    // Source (borrower): decrease
                    decreaseSourceAccount: true,
                    // Destination (capital provider): no-op
                    increaseDestinationAccount: false,
                },
                // *** Debt ***
                // Outstanding balance: decrease
                debt: {
                    organizationId: routing.destination.organizationId,
                    amount: -1 * pendingRequest.amount,
                },
            };
        }
        // Paydown (required)
        case 'curePaydown': {
            // Find associated request routing
            const routing = requestRoutings.find((requestRouting) => requestRouting.type === pendingRequest.type &&
                requestRouting.source.accountId ===
                    pendingRequest.sourceBankAccountId);
            (0, common_1.finleyAssert)(routing, `Routing for cure paydown was not found`);
            return {
                // *** Cash ***
                cash: {
                    // Source (borrower): decrease
                    decreaseSourceAccount: true,
                    // Destination (capital provider): no-op
                    increaseDestinationAccount: false,
                },
                // *** Debt ***
                // Outstanding balance: decrease
                debt: {
                    organizationId: routing.destination.organizationId,
                    amount: -1 * pendingRequest.amount,
                },
            };
        }
        // Recycle
        case 'collectionsRecycle': {
            return {
                // *** Cash ***
                cash: {
                    // Source (borrower): decrease
                    decreaseSourceAccount: true,
                    // Destination (borrower): increase
                    increaseDestinationAccount: true,
                },
                // *** Debt ***
                // Outstanding balance: no-op
                debt: {
                    organizationId: null,
                    amount: 0,
                },
            };
        }
        // Default
        default:
            return (0, common_1.finleyAssertUnreachable)(pendingRequest.type);
    }
};
/**
 * ***** PRO FORMA BORROWING BASE *****
 * Funding actions need not only the report itself, but also the components.
 * Apply any pro forma requests to the cash balances and outstanding balances.
 * Afterwards, update the report using the adjusted outstanding balance and cash balances.
 * The receivable base for the report should not change as a result of these actions.
 * The final pro forma state is representative of cash/debt after settled cash transfers.
 */
const getBorrowingBase = ({ borrowingBaseReport: inputBorrowingBaseReport, cashBalances: inputCashBalances, outstandingBalances: inputOutstandingBalances, pendingRequests, requestRoutings, }) => {
    // Clone inputs for local usage
    const borrowingBaseReport = _.cloneDeep(inputBorrowingBaseReport);
    const cashBalances = _.cloneDeep(inputCashBalances);
    const outstandingBalances = _.cloneDeep(inputOutstandingBalances);
    // Validate debt matches principal balance from report
    const currentOutstandingBalance = common_1.utils.jsRound({
        value: _.sumBy(outstandingBalances, (outstandingBalance) => outstandingBalance.amount),
        precision: 2,
    });
    // We should likely be saving BBR's current principal balance in the DB
    // with only 2 decimal points as well (no fractional cents), but just
    // rounding here for safety for now to manage the below assertion
    const roundedBbrOutstandingBalance = common_1.utils.jsRound({
        value: borrowingBaseReport.currentPrincipalBalance,
        precision: 2,
    });
    (0, common_1.finleyAssert)(currentOutstandingBalance === roundedBbrOutstandingBalance, `Sum of current outstanding loan balances ($${currentOutstandingBalance}) from the ledger does not match the principal balance ($${roundedBbrOutstandingBalance}) contained within the borrowing base report [borrowingBaseReportId:${borrowingBaseReport.id}].`);
    // Apply pro forma requests
    for (const pendingRequest of pendingRequests) {
        // Retrieve expected changes
        const adjustments = getExpectedAdjustments(pendingRequest, requestRoutings);
        // Adjust source cash balance (if available), using amount from request
        const sourceAccount = cashBalances.find((cashBalance) => cashBalance.bankAccountId === pendingRequest.sourceBankAccountId);
        if (sourceAccount && adjustments.cash.decreaseSourceAccount) {
            sourceAccount.balance = common_1.utils.jsRound({
                value: sourceAccount.balance - pendingRequest.amount,
                precision: 2,
            });
        }
        // Adjust destination cash balance (if available), using amount from request
        const destinationAccount = cashBalances.find((cashBalance) => cashBalance.bankAccountId === pendingRequest.destinationBankAccountId);
        if (destinationAccount && adjustments.cash.increaseDestinationAccount) {
            destinationAccount.balance = common_1.utils.jsRound({
                value: destinationAccount.balance + pendingRequest.amount,
                precision: 2,
            });
        }
        // Adjust outstanding balance (if available), using amount derived from adjustment logic
        const capitalProviderOutstandingBalance = outstandingBalances.find((outstandingBalance) => outstandingBalance.organizationId === adjustments.debt.organizationId);
        if (capitalProviderOutstandingBalance) {
            capitalProviderOutstandingBalance.amount = common_1.utils.jsRound({
                value: capitalProviderOutstandingBalance.amount + adjustments.debt.amount,
                precision: 2,
            });
        }
    }
    // Recalculate principal balance using updated loan balances
    const proformaOutstandingBalance = common_1.utils.jsRound({
        value: _.sumBy(outstandingBalances, (outstandingBalance) => outstandingBalance.amount),
        precision: 2,
    });
    // Update principal balance of report
    borrowingBaseReport.currentPrincipalBalance = proformaOutstandingBalance;
    const insightCalcs = (0, borrowingBase_1.generateBorrowingBaseInsightCalcs)({
        bankAccountCashBalances: cashBalances,
        currentPrincipalBalance: proformaOutstandingBalance,
        assetValuation: borrowingBaseReport.assetValuation,
        borrowingBaseAmountAssets: borrowingBaseReport.borrowingBaseAmountAssets,
        maxReceivableAdvanceAmount: borrowingBaseReport.maxReceivableAdvanceAmount,
        commitmentAmount: borrowingBaseReport.commitmentAmount,
        settlementDate: borrowingBaseReport.settlementDate ?? null,
        totalReceivableExposure: borrowingBaseReport.totalReceivableExposure,
        reportingDate: borrowingBaseReport.reportingDate,
    });
    borrowingBaseReport.totalCash = insightCalcs.totalCash;
    borrowingBaseReport.totalBorrowingBaseAmount =
        insightCalcs.totalBorrowingBaseAmount;
    borrowingBaseReport.maxCashAdvanceAmount = insightCalcs.maxCashAdvanceAmount;
    borrowingBaseReport.totalMaxAdvanceAmount =
        insightCalcs.totalMaxAdvanceAmount;
    borrowingBaseReport.borrowingBaseUtilization =
        insightCalcs.borrowingBaseUtilization;
    borrowingBaseReport.totalFacilityUtilization =
        insightCalcs.totalFacilityUtilization;
    // Recalculate cash attributes using updated cash balances
    const proformaBorrowingBaseKeyCashAttributes = (0, borrowingBase_1.calculateBorrowingBaseKeyCashAttributes)(cashBalances, {
        borrowingBaseReserveAmount: borrowingBaseReport.borrowingBaseReserveAmount,
        isBorrowingBaseReservePositiveExcessCollectionsOverReserve: borrowingBaseReport.isBorrowingBaseReservePositiveExcessCollectionsOverReserve,
    });
    const proformaBorrowingBaseAvailabilityAttributes = (0, borrowingBase_1.calculateBorrowingBaseAvailabilityAttributes)({
        totalReceivableBorrowingBaseAmount: borrowingBaseReport.maxReceivableAdvanceAmount,
        totalCashBorrowingBaseAmount: proformaBorrowingBaseKeyCashAttributes.totalCashBorrowingBaseAmount,
        commitmentAmount: borrowingBaseReport.commitmentAmount,
        currentPrincipalBalance: borrowingBaseReport.currentPrincipalBalance,
    });
    const proformaBorrowingBaseReport = {
        ...borrowingBaseReport,
        ...proformaBorrowingBaseKeyCashAttributes,
        ...proformaBorrowingBaseAvailabilityAttributes,
    };
    return {
        isProforma: pendingRequests.length > 0,
        borrowingBaseReport: proformaBorrowingBaseReport,
        cashBalances,
        outstandingBalances,
        pendingRequests,
    };
};
exports.getBorrowingBase = getBorrowingBase;
//# sourceMappingURL=borrowingBase.js.map